import { useState, useEffect, useRef } from "react";
import { isActiveConvo, playPing, showMsgNotification, unlockAudio, requestNotifPermission } from "@/lib/msgNotify";
import MenuCustomizer, { MenuHiddenStyle, MenuCustomizeButton, FavoritesBar, collectItems, mergeCache } from "@/components/MenuCustomizer";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import LegalFooter from "@/components/LegalFooter";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Users, KeyRound, ScrollText, LogOut, UserCog, LayoutDashboard, Menu, ShieldCheck, Ban, Sparkles, Building2, FileSignature, PackageCheck, ClipboardList, BookOpenText, Radio, FileText, ClipboardCheck, Calculator, CalendarDays, Palmtree, MessageSquare, Truck, Nfc, Shirt, Car, ShieldAlert, Target, Receipt, BellRing, Bot, Route, FolderOpen, FileSpreadsheet, Briefcase, ChevronDown, ChevronRight, HardHat, MessageSquareWarning, Gavel, BookOpen, ArrowLeftRight, Sun, Moon, Wallet, ListChecks, CalendarClock, Percent, FileBarChart } from "lucide-react";

const SEC_PATHS = ["/dashboard/decompte-paie", "/dashboard/paie-ccn", "/dashboard/conges", "/dashboard/devis", "/dashboard/prospection", "/dashboard/notes-frais", "/dashboard/relances", "/dashboard/amendes", "/dashboard/reclamations", "/dashboard/documents-societe", "/dashboard/controle-devis", "/dashboard/controle-facture", "/dashboard/comptes-rendus-interventions", "/dashboard/comptes-rendus-gardiennage", "/dashboard/comptes-rendus-rondes", "/dashboard/assistant"];
const EXP_PATHS = ["/dashboard/clients", "/dashboard/suivi-cles", "/dashboard/points-nfc", "/dashboard/pti", "/dashboard/suivi-rondes", "/dashboard/cles-envoyees", "/dashboard/consignes", "/dashboard/passation", "/dashboard/planning", "/dashboard/soustraitants", "/dashboard/mes-missions", "/dashboard/suivi-gardiennage", "/dashboard/intervention", "/dashboard/agents", "/dashboard/comptes-clients", "/dashboard/dotations", "/dashboard/vehicules", "/dashboard/carnet-de-bord-suivi", "/dashboard/presence", "/dashboard/suivi"];
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import AlertsBell from "@/components/AlertsBell";
import NotificationsBell from "@/components/NotificationsBell";
import { SpaceSwitcher } from "@/components/SpaceSwitcher";
import { SpaceBrand } from "@/components/SpaceBrand";
import ConsignesBanner from "@/components/ConsignesBanner";
import RgpdConsentBanner from "@/components/RgpdConsentBanner";
import NextMissionBanner from "@/components/NextMissionBanner";
import MessagesBanner from "@/components/MessagesBanner";
import PTIAlertBanner from "@/components/PTIAlertBanner";
import OfflineBanner from "@/components/OfflineBanner";
import { HelpAiWidget } from "@/components/HelpAiWidget";
import { ReportsAuthBanner } from "@/components/ReportsAuthBanner";

const navItemClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
    isActive ? "bg-[#007AFF]/15 text-white border border-[#007AFF]/30" : "text-zinc-400 hover:bg-zinc-900 hover:text-white border border-transparent"
  }`;

export default function Layout() {
  const { user, logout, setAgentMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager" || isAdmin || user?.role === "demo";
  const isSuper = user?.platform_role === "super_admin";
  const isGardiennage = user?.role === "gardiennage";
  const isRondier = user?.agent_type === "rondier";
  const isAgentUser = user?.role === "agent" || isGardiennage;
  const isChef = user?.role === "chef_poste";
  const isSecretaire = user?.role === "secretaire";
  const isClient = user?.role === "client";
  const can = (m) => isManager || (user?.permissions || []).includes(m);
  const mgrTabs = user?.tab_permissions;
  const canTab = (key) => isAdmin || (isManager ? (Array.isArray(mgrTabs) ? mgrTabs.includes(key) : true) : false);
  const secOk = user?.role !== "manager" || !!user?.acces_secretariat;
  const expOk = user?.role !== "manager" || user?.acces_exploitation !== false;
  const [mobileOpen, setMobileOpen] = useState(false);
  const isStaffTheme = (["admin", "manager", "demo"].includes(user?.role)) && !user?.agent_mode;
  const [theme, setTheme] = useState(() => localStorage.getItem("dash_theme") || "light");
  useEffect(() => {
    document.documentElement.classList.toggle("theme-light", isStaffTheme && theme === "light");
    return () => document.documentElement.classList.remove("theme-light");
  }, [isStaffTheme, theme]);
  const toggleTheme = () => setTheme((t) => { const n = t === "light" ? "dark" : "light"; localStorage.setItem("dash_theme", n); return n; });
  const [secOpen, setSecOpen] = useState(() => SEC_PATHS.some((p) => window.location.pathname.startsWith(p)));
  const [expOpen, setExpOpen] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPrefs, setMenuPrefs] = useState({ hidden: [], favorites: [], source: "role" });
  useEffect(() => {
    if (!user) return;
    api.get("/auth/menu-prefs").then(({ data }) => setMenuPrefs(data)).catch(() => {});
    // Signale les onglets visibles pour que l'admin connaisse le menu de chaque rôle.
    const t = setTimeout(() => {
      const found = collectItems();
      mergeCache(found);
      const sig = `${user.role}|${found.map((i) => i.id).join(",")}`;
      if (found.length && localStorage.getItem("menu_catalog_sig") !== sig) {
        api.post("/auth/menu-catalog", { items: found }).then(() => localStorage.setItem("menu_catalog_sig", sig)).catch(() => {});
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [user?.user_id, user?.role]);
  useEffect(() => { if (SEC_PATHS.some((p) => location.pathname.startsWith(p))) setSecOpen(true); }, [location.pathname]);
  useEffect(() => { if (EXP_PATHS.some((p) => location.pathname.startsWith(p))) setExpOpen(true); }, [location.pathname]);
  const [sgCount, setSgCount] = useState(0);
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [unreadConsignes, setUnreadConsignes] = useState(0);
  const [unreadRec, setUnreadRec] = useState(0);

  useEffect(() => {
    if (!isAgentUser) return;
    let active = true;
    const load = () => api.get("/reclamations/mine/unread-count").then(({ data }) => { if (active) setUnreadRec(data?.count || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { active = false; clearInterval(t); };
    // eslint-disable-next-line
  }, [user, location.pathname]);

  useEffect(() => {
    if (isSuper || isClient) return;
    let active = true;
    unlockAudio();
    requestNotifPermission();
    const load = () => api.get("/messages/unread").then(({ data }) => {
      if (!active) return;
      setUnreadMsg(data?.total || 0);
      notifyNew(data?.senders || []);
    }).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => { active = false; clearInterval(t); };
    // eslint-disable-next-line
  }, [user]);

  const openConvo = (id, name) => navigate("/dashboard/messages", { state: { openUserId: id, openUserName: name } });
  const seenRef = useRef(null);
  const notifyNew = (senders) => {
    const prev = seenRef.current;
    seenRef.current = Object.fromEntries(senders.map((s) => [s.from_id, s.last_at]));
    if (!prev) return;
    const fresh = senders.filter((s) => s.last_at > (prev[s.from_id] || "") && !isActiveConvo(s.from_id));
    if (!fresh.length) return;
    playPing();
    fresh.forEach((s) => showMsgNotification(s, () => openConvo(s.from_id, s.from_name)));
  };

  useEffect(() => {
    const sw = navigator.serviceWorker;
    if (!sw) return;
    const onMsg = (e) => { if (e.data?.type === "open-convo") openConvo(e.data.userId, e.data.name); };
    sw.addEventListener("message", onMsg);
    return () => sw.removeEventListener("message", onMsg);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user?.role !== "agent") return;
    let active = true;
    const load = () => api.get("/consignes/mine").then(({ data }) => {
      if (active) setUnreadConsignes((data || []).filter((c) => !(c.read_by || []).includes(user?.user_id)).length);
    }).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { active = false; clearInterval(t); };
    // eslint-disable-next-line
  }, [user]);

  const MsgBadge = () => (unreadMsg > 0 ? (
    <span data-testid="nav-messages-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-semibold flex items-center justify-center">{unreadMsg}</span>
  ) : null);
  const MessagesLink = () => (
    <NavLink to="/dashboard/messages" onClick={closeMobile} className={navItemClass} data-testid="nav-messages">
      <MessageSquare className="w-4 h-4" /> Messagerie <MsgBadge />
    </NavLink>
  );

  useEffect(() => {
    if (!canTab("suivi_gardiennage")) return;
    let active = true;
    const load = () => api.get("/gardiennage/active").then(({ data }) => { if (active) setSgCount((data || []).length); }).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => { active = false; clearInterval(t); };
    // eslint-disable-next-line
  }, [user]);

  const switchMode = async (on) => {
    closeMobile();
    await setAgentMode(on);
    navigate("/dashboard", { replace: true });
  };
  const ModeSwitch = ({ on }) => (
    <button type="button" onClick={() => switchMode(on)} data-testid={on ? "switch-to-agent-mode" : "switch-to-manager-mode"}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-[#FFB020] border border-[#FFB020]/30 bg-[#FFB020]/5 hover:bg-[#FFB020]/15 transition-colors">
      <ArrowLeftRight className="w-4 h-4" /> {on ? "Passer en mode agent" : "Revenir en mode gestion"}
    </button>
  );

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  const closeMobile = () => setMobileOpen(false);

  const NavLinks = () => {
    if (isClient) {
      return (
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1 [scrollbar-width:thin]">
          <NavLink to="/dashboard/mes-sites" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-sites">
            <Building2 className="w-4 h-4" /> Mes sites
          </NavLink>
          <NavLink to="/dashboard/mes-comptes-rendus" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-comptes-rendus">
            <FileBarChart className="w-4 h-4" /> Mes comptes rendus
          </NavLink>
          <NavLink to="/dashboard/profil" onClick={closeMobile} className={navItemClass} data-testid="nav-profil">
            <UserCog className="w-4 h-4" /> Profil
          </NavLink>
        </nav>
      );
    }
    if (isSecretaire) {
      return (
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1 [scrollbar-width:thin]">
          <NavLink to="/dashboard/devis" onClick={closeMobile} className={navItemClass} data-testid="nav-devis">
            <ClipboardList className="w-4 h-4" /> Devis
          </NavLink>
          {user?.acces_tva && (
            <NavLink to="/dashboard/tva" onClick={closeMobile} className={navItemClass} data-testid="nav-tva">
              <Percent className="w-4 h-4" /> TVA
            </NavLink>
          )}
          <MessagesLink />
          <NavLink to="/dashboard/profil" onClick={closeMobile} className={navItemClass} data-testid="nav-profil">
            <UserCog className="w-4 h-4" /> Profil
          </NavLink>
        </nav>
      );
    }
    if (isChef) {
      return (
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1 [scrollbar-width:thin]">
          <NavLink to="/dashboard/chef" onClick={closeMobile} className={navItemClass} data-testid="nav-chef">
            <Building2 className="w-4 h-4" /> Mon poste
          </NavLink>
          <NavLink to="/dashboard/mes-missions" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-missions">
            <CalendarDays className="w-4 h-4" /> Mon planning du mois
          </NavLink>
          <NavLink to="/dashboard/chef-agents" onClick={closeMobile} className={navItemClass} data-testid="nav-chef-agents">
            <UserCog className="w-4 h-4" /> Mes agents
          </NavLink>
          <NavLink to="/dashboard/planning" onClick={closeMobile} className={navItemClass} data-testid="nav-planning">
            <CalendarDays className="w-4 h-4" /> Planification
          </NavLink>
          <NavLink to="/dashboard/consignes" onClick={closeMobile} className={navItemClass} data-testid="nav-consignes">
            <ClipboardList className="w-4 h-4" /> Consignes
          </NavLink>
          <NavLink to="/dashboard/conges" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-conges">
            <Palmtree className="w-4 h-4" /> Mes congés
          </NavLink>
          <MessagesLink />
          <NavLink to="/dashboard/profil" onClick={closeMobile} className={navItemClass} data-testid="nav-profil">
            <UserCog className="w-4 h-4" /> Profil
          </NavLink>
        </nav>
      );
    }
    if (isAgentUser) {
      const mods = user?.agent_modules || [];
      const has = (m) => mods.includes(m);
      return (
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1 [scrollbar-width:thin]">
          {user?.agent_mode && <ModeSwitch on={false} />}
          <NavLink to="/dashboard/accueil" onClick={closeMobile} className={navItemClass} data-testid="nav-accueil-agent">
            <LayoutDashboard className="w-4 h-4" /> Accueil
          </NavLink>
          {has("gardiennage") && (
          <NavLink to="/dashboard/mod/gardiennage" onClick={closeMobile} className={navItemClass} data-testid="nav-mod-gardiennage">
            <ShieldCheck className="w-4 h-4" /> Gardiennage
          </NavLink>)}
          {has("intervention") && (
          <NavLink to="/dashboard/mod/intervention" onClick={closeMobile} className={navItemClass} data-testid="nav-mod-intervention">
            <FileText className="w-4 h-4" /> Intervention
          </NavLink>)}
          {has("intervention") && (
          <NavLink to="/dashboard/clients" onClick={closeMobile} className={navItemClass} data-testid="nav-clients-agent">
            <Building2 className="w-4 h-4" /> Clients et sites
          </NavLink>)}
          {has("ronde") && (
          <NavLink to="/dashboard/mod/ronde" onClick={closeMobile} className={navItemClass} data-testid="nav-mod-ronde">
            <Nfc className="w-4 h-4" /> Ronde
          </NavLink>)}
          <div className="my-2 border-t border-white/10" />
          <MessagesLink />
          <NavLink to="/dashboard/mes-missions" onClick={closeMobile} className={navItemClass} data-testid="nav-planning">
            <CalendarDays className="w-4 h-4" /> Planning
          </NavLink>
          <NavLink to="/dashboard/conges" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-conges">
            <Palmtree className="w-4 h-4" /> Congés
          </NavLink>
          <NavLink to="/dashboard/mes-dotations" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-dotations">
            <Shirt className="w-4 h-4" /> Mes équipements
          </NavLink>
          <NavLink to="/dashboard/mes-notes-frais" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-notes-frais">
            <Receipt className="w-4 h-4" /> Mes notes de frais
          </NavLink>
          <NavLink to="/dashboard/mes-amendes" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-amendes">
            <Gavel className="w-4 h-4" /> Mes amendes
          </NavLink>
          <NavLink to="/guide" onClick={closeMobile} className={navItemClass} data-testid="nav-guide">
            <BookOpen className="w-4 h-4" /> Guide d'utilisation
          </NavLink>
          <NavLink to="/dashboard/consignes" onClick={closeMobile} className={navItemClass} data-testid="nav-consignes-agent">
            <ClipboardList className="w-4 h-4" /> Consignes du jour
            {unreadConsignes > 0 && <span data-testid="nav-consignes-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-semibold flex items-center justify-center">{unreadConsignes}</span>}
          </NavLink>
          <NavLink to="/dashboard/mes-reclamations" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-reclamations">
            <MessageSquareWarning className="w-4 h-4" /> Réclamations & consignes
            {unreadRec > 0 && <span data-testid="nav-mes-reclamations-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF3B30] text-white text-[11px] font-semibold flex items-center justify-center">{unreadRec}</span>}
          </NavLink>
          <NavLink to="/dashboard/pti" onClick={closeMobile} className={navItemClass} data-testid="nav-pti">
            <ShieldAlert className="w-4 h-4" /> PTI
          </NavLink>
          <NavLink to="/dashboard/profil" onClick={closeMobile} className={navItemClass} data-testid="nav-profil">
            <UserCog className="w-4 h-4" /> Profil
          </NavLink>
        </nav>
      );
    }
    return (
    <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1 [scrollbar-width:thin]">
      {canTab("dashboard") && (
        <NavLink to="/dashboard" end onClick={closeMobile} className={navItemClass} data-testid="nav-dashboard">
          <LayoutDashboard className="w-4 h-4" /> Tableau de bord
        </NavLink>
      )}
      {["manager", "admin"].includes(user?.role) && user?.linked_agent_id && <ModeSwitch on />}
      {!isSuper && <MessagesLink />}
      {!isSuper && isManager && (
        <NavLink to="/dashboard/appels-offres" onClick={closeMobile} className={navItemClass} data-testid="nav-appels-offres">
          <FileSignature className="w-4 h-4" /> Appels d'offres
        </NavLink>
      )}
      {isSuper ? (
        <NavLink to="/dashboard/entreprises" onClick={closeMobile} className={navItemClass} data-testid="nav-entreprises">
          <Building2 className="w-4 h-4" /> Entreprises
        </NavLink>
      ) : (
      <>
      {isManager && expOk && (
        <button type="button" onClick={() => setExpOpen((o) => !o)} data-testid="nav-exploitation-toggle"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-zinc-400 hover:bg-zinc-900 hover:text-white border border-transparent transition-colors">
          <HardHat className="w-4 h-4" /> Exploitation
          {expOpen ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
        </button>
      )}
      {(!isManager || (expOk && expOpen)) && (
      <div className="ml-3 pl-2 border-l border-white/10 space-y-1 mt-1" data-testid="nav-exploitation-group">
      {(isManager ? canTab("clients") : can("clients")) && (
      <NavLink to="/dashboard/clients" onClick={closeMobile} className={navItemClass} data-testid="nav-clients">
        <Users className="w-4 h-4" /> Clients
      </NavLink>)}
      {(isManager ? canTab("suivi_cles") : can("suivi_cles")) && (
      <NavLink to="/dashboard/suivi-cles" onClick={closeMobile} className={navItemClass} data-testid="nav-suivi-cles">
        <KeyRound className="w-4 h-4" /> Suivi des clés
      </NavLink>)}
      {(user?.role === "admin" || user?.role === "manager") && (
      <NavLink to="/dashboard/points-nfc" onClick={closeMobile} className={navItemClass} data-testid="nav-points-nfc">
        <Nfc className="w-4 h-4" /> Points NFC
      </NavLink>)}
      {(user?.role === "admin" || user?.role === "manager") && (
      <NavLink to="/dashboard/pti" onClick={closeMobile} className={navItemClass} data-testid="nav-pti">
        <ShieldAlert className="w-4 h-4" /> PTI (Travailleur isolé)
      </NavLink>)}
      {(user?.role === "admin" || user?.role === "manager") && (
      <NavLink to="/dashboard/rondes" onClick={closeMobile} className={navItemClass} data-testid="nav-rondes">
        <Route className="w-4 h-4" /> Rondes
      </NavLink>)}
      {(user?.role === "admin" || user?.role === "manager") && (
      <NavLink to="/dashboard/suivi-rondes" onClick={closeMobile} className={navItemClass} data-testid="nav-suivi-rondes">
        <Radio className="w-4 h-4" /> Suivi des rondes
      </NavLink>)}
      {(isManager ? canTab("cles_envoyees") : can("cles_envoyees")) && (
      <NavLink to="/dashboard/cles-envoyees" onClick={closeMobile} className={navItemClass} data-testid="nav-cles-envoyees">
        <PackageCheck className="w-4 h-4" /> Clés envoyées
      </NavLink>)}
      {(isManager ? canTab("consignes") : can("consignes")) && (
      <NavLink to="/dashboard/consignes" onClick={closeMobile} className={navItemClass} data-testid="nav-consignes">
        <ClipboardList className="w-4 h-4" /> Consignes du jour
      </NavLink>)}
      {(isManager ? canTab("passation") : can("passation")) && (
      <NavLink to="/dashboard/passation" onClick={closeMobile} className={navItemClass} data-testid="nav-passation">
        <BookOpenText className="w-4 h-4" /> Passation
      </NavLink>)}
      {canTab("planning") && (
      <NavLink to="/dashboard/planning" onClick={closeMobile} className={navItemClass} data-testid="nav-planning">
        <CalendarDays className="w-4 h-4" /> Planning
      </NavLink>)}
      {canTab("soustraitants") && (
      <NavLink to="/dashboard/soustraitants" onClick={closeMobile} className={navItemClass} data-testid="nav-soustraitants">
        <Truck className="w-4 h-4" /> Sous-traitants
      </NavLink>)}
      {(user?.role === "admin" || user?.role === "manager") && (
      <NavLink to="/dashboard/mes-missions" onClick={closeMobile} className={navItemClass} data-testid="nav-mon-planning">
        <CalendarDays className="w-4 h-4" /> Mon planning
      </NavLink>)}
      {canTab("suivi_gardiennage") && (
      <NavLink to="/dashboard/suivi-gardiennage" onClick={closeMobile} className={navItemClass} data-testid="nav-suivi-gardiennage">
        <Radio className="w-4 h-4" /> Suivi gardiennage
        {sgCount > 0 && (
          <span data-testid="nav-sg-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-black text-[11px] font-semibold flex items-center justify-center">{sgCount}</span>
        )}
      </NavLink>)}
      {(isGardiennage || user?.role === "agent") && (
      <NavLink to="/dashboard/accueil" onClick={closeMobile} className={navItemClass} data-testid="nav-accueil">
        <LayoutDashboard className="w-4 h-4" /> Accueil
      </NavLink>)}
      {(isGardiennage || user?.role === "agent") && (
      <NavLink to="/dashboard/gardiennage" onClick={closeMobile} className={navItemClass} data-testid="nav-gardiennage">
        <ShieldCheck className="w-4 h-4" /> Gardiennage sur site
      </NavLink>)}
      {(isGardiennage || user?.role === "agent") && (
      <NavLink to="/dashboard/mes-missions" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-missions">
        <CalendarDays className="w-4 h-4" /> Mon planning du mois
      </NavLink>)}
      {(isGardiennage || user?.role === "agent") && (
      <NavLink to="/dashboard/missions-cloturees" onClick={closeMobile} className={navItemClass} data-testid="nav-missions-cloturees">
        <ClipboardCheck className="w-4 h-4" /> Missions clôturées
      </NavLink>)}
      {(isGardiennage || user?.role === "agent") && (
      <NavLink to="/dashboard/conges" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-conges">
        <Palmtree className="w-4 h-4" /> Mes congés
      </NavLink>)}
      {(isGardiennage || user?.role === "agent") && (
      <NavLink to="/dashboard/mes-dotations" onClick={closeMobile} className={navItemClass} data-testid="nav-mes-dotations">
        <Shirt className="w-4 h-4" /> Mes équipements
      </NavLink>)}
      {(isGardiennage || user?.role === "agent") && (
      <NavLink to="/dashboard/carnet-de-bord" onClick={closeMobile} className={navItemClass} data-testid="nav-carnet-de-bord">
        <Car className="w-4 h-4" /> Carnet de bord
      </NavLink>)}
      {(!isGardiennage || user?.allow_ronde) && (
      <NavLink to="/dashboard/intervention" onClick={closeMobile} className={navItemClass} data-testid="nav-intervention">
        <FileText className="w-4 h-4" /> Compte rendu
      </NavLink>)}
      {isManager && (
        <>
          {canTab("agents") && (
          <NavLink to="/dashboard/agents" onClick={closeMobile} className={navItemClass} data-testid="nav-agents">
            <UserCog className="w-4 h-4" /> Agents
          </NavLink>)}
          <NavLink to="/dashboard/comptes-clients" onClick={closeMobile} className={navItemClass} data-testid="nav-comptes-clients">
            <Users className="w-4 h-4" /> Comptes clients
          </NavLink>
          <NavLink to="/dashboard/dotations" onClick={closeMobile} className={navItemClass} data-testid="nav-dotations">
            <Shirt className="w-4 h-4" /> Dotations
          </NavLink>
          <NavLink to="/dashboard/vehicules" onClick={closeMobile} className={navItemClass} data-testid="nav-vehicules">
            <Car className="w-4 h-4" /> Véhicules
          </NavLink>
          <NavLink to="/dashboard/carnet-de-bord-suivi" onClick={closeMobile} className={navItemClass} data-testid="nav-carnet-suivi">
            <Route className="w-4 h-4" /> Carnet de bord
          </NavLink>
          {canTab("presence") && (
          <NavLink to="/dashboard/presence" onClick={closeMobile} className={navItemClass} data-testid="nav-presence">
            <Radio className="w-4 h-4" /> Présence agents
          </NavLink>)}
          {canTab("suivi") && (
          <NavLink to="/dashboard/suivi" onClick={closeMobile} className={navItemClass} data-testid="nav-suivi">
            <ScrollText className="w-4 h-4" /> Suivi &amp; Traçabilité
          </NavLink>)}
        </>
      )}
      </div>)}
      {isManager && secOk && (
        <>
          <button type="button" onClick={() => setSecOpen((o) => !o)} data-testid="nav-secretariat-toggle"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-zinc-400 hover:bg-zinc-900 hover:text-white border border-transparent transition-colors">
            <Briefcase className="w-4 h-4" /> Secrétariat
            {secOpen ? <ChevronDown className="w-4 h-4 ml-auto" /> : <ChevronRight className="w-4 h-4 ml-auto" />}
          </button>
          {secOpen && (
          <div className="ml-3 pl-2 border-l border-white/10 space-y-1 mt-1" data-testid="nav-secretariat-group">
            {canTab("decompte_paie") && (
            <NavLink to="/dashboard/decompte-paie" onClick={closeMobile} className={navItemClass} data-testid="nav-decompte-paie">
              <Calculator className="w-4 h-4" /> Décompte paie
            </NavLink>)}
            {canTab("paie_ccn") && (
            <NavLink to="/dashboard/paie-ccn" onClick={closeMobile} className={navItemClass} data-testid="nav-paie-ccn">
              <Calculator className="w-4 h-4" /> Paie CCN 1351
            </NavLink>)}
            {canTab("conges") && (
            <NavLink to="/dashboard/conges" onClick={closeMobile} className={navItemClass} data-testid="nav-conges">
              <Palmtree className="w-4 h-4" /> Congés
            </NavLink>)}
            {(isAdmin || canTab("devis")) && (
            <NavLink to="/dashboard/devis" onClick={closeMobile} className={navItemClass} data-testid="nav-devis">
              <ClipboardList className="w-4 h-4" /> Devis
            </NavLink>)}
            {(isAdmin || canTab("prospection")) && (
            <NavLink to="/dashboard/prospection" onClick={closeMobile} className={navItemClass} data-testid="nav-prospection">
              <Target className="w-4 h-4" /> Prospection
            </NavLink>)}
            {(isAdmin || canTab("notes_frais")) && (
            <NavLink to="/dashboard/notes-frais" onClick={closeMobile} className={navItemClass} data-testid="nav-notes-frais">
              <Receipt className="w-4 h-4" /> Notes de frais
            </NavLink>)}
            {(isAdmin || canTab("relances")) && (
            <NavLink to="/dashboard/relances" onClick={closeMobile} className={navItemClass} data-testid="nav-relances">
              <BellRing className="w-4 h-4" /> Relances
            </NavLink>)}
            {isManager && (
            <NavLink to="/dashboard/amendes" onClick={closeMobile} className={navItemClass} data-testid="nav-amendes">
              <Gavel className="w-4 h-4" /> Amendes
            </NavLink>)}
            {isManager && (
            <NavLink to="/dashboard/reclamations" onClick={closeMobile} className={navItemClass} data-testid="nav-reclamations">
              <MessageSquareWarning className="w-4 h-4" /> Réclamations & Consignes
            </NavLink>)}
            {(isManager || isSecretaire || isChef) && (
            <NavLink to="/dashboard/documents-societe" onClick={closeMobile} className={navItemClass} data-testid="nav-documents-societe">
              <FolderOpen className="w-4 h-4" /> Documents société
            </NavLink>)}
            {(isAdmin || isSecretaire || canTab("controle_devis")) && (
            <NavLink to="/dashboard/controle-devis" onClick={closeMobile} className={navItemClass} data-testid="nav-controle-devis">
              <ClipboardCheck className="w-4 h-4" /> Contrôle devis
            </NavLink>)}
            {(isAdmin || isSecretaire || canTab("controle_facture")) && (
            <NavLink to="/dashboard/controle-facture" onClick={closeMobile} className={navItemClass} data-testid="nav-controle-facture">
              <FileSpreadsheet className="w-4 h-4" /> Contrôle facture
            </NavLink>)}
            {(isAdmin || isSecretaire || canTab("facturation_gardiennage")) && (
            <NavLink to="/dashboard/facturation-gardiennage" onClick={closeMobile} className={navItemClass} data-testid="nav-facturation-gardiennage">
              <Calculator className="w-4 h-4" /> Facturation gardiennage
            </NavLink>)}
            {canTab("cr_interventions") && (
            <NavLink to="/dashboard/comptes-rendus-interventions" onClick={closeMobile} className={navItemClass} data-testid="nav-comptes-rendus-interventions">
              <ClipboardCheck className="w-4 h-4" /> Comptes rendus interventions
            </NavLink>)}
            {canTab("cr_gardiennage") && (
            <NavLink to="/dashboard/comptes-rendus-gardiennage" onClick={closeMobile} className={navItemClass} data-testid="nav-comptes-rendus-gardiennage">
              <ShieldCheck className="w-4 h-4" /> Comptes rendus gardiennage
            </NavLink>)}
            {canTab("cr_interventions") && (
            <NavLink to="/dashboard/comptes-rendus-rondes" onClick={closeMobile} className={navItemClass} data-testid="nav-comptes-rendus-rondes">
              <Route className="w-4 h-4" /> Comptes rendus rondes
            </NavLink>)}
            {isAdmin && (
            <NavLink to="/dashboard/assistant" onClick={closeMobile} className={navItemClass} data-testid="nav-assistant">
              <Bot className="w-4 h-4" /> Assistant PACAZUR
            </NavLink>)}
          </div>)}
        </>
      )}
      {(isAdmin || (user?.role === "manager" && user?.acces_a_payer)) && (
        <NavLink to="/dashboard/a-payer" onClick={closeMobile} className={navItemClass} data-testid="nav-a-payer">
          <Wallet className="w-4 h-4" /> À payer
        </NavLink>
      )}
      {(isAdmin || ((user?.role === "manager" || isSecretaire) && user?.acces_tva)) && (
        <NavLink to="/dashboard/tva" onClick={closeMobile} className={navItemClass} data-testid="nav-tva">
          <Percent className="w-4 h-4" /> TVA
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/dashboard/rapports-mensuels" onClick={closeMobile} className={navItemClass} data-testid="nav-rapports-mensuels">
          <FileBarChart className="w-4 h-4" /> Rapports mensuels
        </NavLink>
      )}
      {(isAdmin || user?.role === "manager") && (
        <NavLink to="/dashboard/taches" onClick={closeMobile} className={navItemClass} data-testid="nav-taches">
          <ListChecks className="w-4 h-4" /> {isAdmin || user?.acces_taches ? "Tâches" : "Mes tâches"}
        </NavLink>
      )}
      {(isAdmin || user?.role === "manager") && (
        <NavLink to="/dashboard/rendez-vous" onClick={closeMobile} className={navItemClass} data-testid="nav-rendez-vous">
          <CalendarClock className="w-4 h-4" /> {isAdmin ? "Rendez-vous" : "Mes rendez-vous"}
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/dashboard/notifications" onClick={closeMobile} className={navItemClass} data-testid="nav-notifications">
          <MessageSquare className="w-4 h-4" /> Notifications
        </NavLink>
      )}
      {(isAdmin || user?.role === "manager") && (
        <NavLink to="/dashboard/journal-emails" onClick={closeMobile} className={navItemClass} data-testid="nav-journal-emails">
          <MessageSquare className="w-4 h-4" /> Journal des emails
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/dashboard/compte-demo" onClick={closeMobile} className={navItemClass} data-testid="nav-demo">
          <LayoutDashboard className="w-4 h-4" /> Compte démo
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/dashboard/chefs-poste" onClick={closeMobile} className={navItemClass} data-testid="nav-chefs-poste">
          <Building2 className="w-4 h-4" /> Chefs de poste
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/dashboard/managers" onClick={closeMobile} className={navItemClass} data-testid="nav-managers">
          <ShieldCheck className="w-4 h-4" /> Managers
        </NavLink>
      )}
      {isAdmin && (
        <NavLink to="/dashboard/contrats" onClick={closeMobile} className={navItemClass} data-testid="nav-contrats">
          <FileSignature className="w-4 h-4" /> Contrats
        </NavLink>
      )}
      </>
      )}
    </nav>
    );
  };

  const UserBlock = () => (
    <div className="p-3 border-t border-white/10 shrink-0">
      <div className="flex items-center gap-3 px-2 py-2 mb-2">
        <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-semibold overflow-hidden shrink-0">
          {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.[0]?.toUpperCase() || "?")}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate" data-testid="current-user-name">{user?.name}</p>
          <p className="text-xs text-[#FFB020] font-code uppercase tracking-wider">{isSuper ? "Super-Admin" : user?.role === "demo" ? "Démo Admin · lecture seule" : user?.is_demo ? "Démo Agent" : isAdmin ? "Administrateur" : isManager ? "Manager" : isChef ? "Chef de poste" : isSecretaire ? "Secrétaire" : isClient ? "Client" : isGardiennage ? "Gardiennage" : (isRondier ? "Rondier" : "Agent")}</p>
        </div>
      </div>
      {isAdmin && (
        <>
        {user?.multi_space_owner && !user?.agent_mode && (
          <NavLink to="/dashboard/espaces" onClick={closeMobile} className={navItemClass} data-testid="nav-espaces">
            <Building2 className="w-4 h-4" /> Espaces entreprise
          </NavLink>
        )}
        <NavLink to="/dashboard/espace-entreprise" onClick={closeMobile} className={navItemClass} data-testid="nav-espace-entreprise">
          <Building2 className="w-4 h-4" /> Espace entreprise
        </NavLink>
        </>
      )}
      <MenuCustomizeButton onOpen={() => setMenuOpen(true)} count={(menuPrefs.hidden || []).length} />
      {isStaffTheme && (
        <Button onClick={toggleTheme} variant="ghost" data-testid="theme-toggle-button" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors">
          {theme === "light" ? <><Moon className="w-4 h-4 mr-2" /> Thème sombre</> : <><Sun className="w-4 h-4 mr-2" /> Thème clair</>}
        </Button>
      )}
      <Button onClick={handleLogout} variant="ghost" data-testid="logout-button" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors">
        <LogOut className="w-4 h-4 mr-2" /> Déconnexion
      </Button>
    </div>
  );

  const Brand = () => <SpaceBrand user={user} />;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <MenuHiddenStyle hidden={menuPrefs.hidden} />
      <MenuCustomizer open={menuOpen} setOpen={setMenuOpen} prefs={menuPrefs} setPrefs={setMenuPrefs} onBeforeOpen={() => { setExpOpen(true); setSecOpen(true); }} onNavigate={closeMobile} />
      {/* Desktop sidebar */}
      <aside data-nav-root className="hidden lg:flex w-64 shrink-0 border-r border-white/10 bg-[#0A0A0A] flex-col fixed h-screen h-[100dvh] z-30">
        <div className="p-5 border-b border-white/10 flex items-center justify-between gap-2 shrink-0">
          <Brand />
          <div className="flex items-center gap-0.5 shrink-0">
            {!isGardiennage && !isChef && !isClient && <NotificationsBell />}
            {isManager && <AlertsBell />}
          </div>
        </div>
        <FavoritesBar favorites={menuPrefs.favorites} />
        <NavLinks />
        <UserBlock />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-4 h-14 border-b border-white/10 bg-[#0A0A0A]">
        <div className="flex items-center gap-2">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button" className="text-white hover:bg-zinc-900">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent data-nav-root side="left" className="w-72 p-0 bg-[#0A0A0A] border-white/10 text-white flex flex-col h-[100dvh] overflow-hidden">
              <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
              <div className="p-5 border-b border-white/10 shrink-0">
                <Brand />
              </div>
              <FavoritesBar favorites={menuPrefs.favorites} onClick={closeMobile} />
              <NavLinks />
              <UserBlock />
            </SheetContent>
          </Sheet>
          <SpaceBrand user={user} compact />
        </div>
        <div className="flex items-center gap-1">
          {!isGardiennage && !isClient && <NotificationsBell />}
          {isManager && <AlertsBell />}
        </div>
      </header>

      {/* Main */}
      <main className="lg:ml-64 min-h-screen">
        {user?.multi_space_owner && !user?.agent_mode && <SpaceSwitcher user={user} />}
        <OfflineBanner />
        <RgpdConsentBanner />
        {!isClient && <><NextMissionBanner />
        <ConsignesBanner />
        <MessagesBanner /></>}
        {(user?.role === "admin" || user?.role === "manager") && <PTIAlertBanner />}
        <ReportsAuthBanner />
        <Outlet />
        {["admin", "manager"].includes(user?.role) && !user?.agent_mode && <HelpAiWidget />}
        <div className="px-6 sm:px-8 py-6 border-t border-white/10 mt-8"><LegalFooter /></div>
      </main>
    </div>
  );
}
